import * as Schema from "effect/Schema";

export const LocalComputerCommand = Schema.Literals([
  "status",
  "install",
  "start",
  "start-background",
  "show",
  "hide",
  "pause",
  "resume",
  // Explicit recovery: cold-boot the kept disk and set aside a saved state that could not be restored.
  "start-fresh",
  // Explicit Try again: the only command that restores a saved state whose restore already failed.
  "retry-restore",
  // Explicit removal of the saved state set aside by start-fresh; nothing else removes it.
  "discard-set-aside",
  "shutdown",
  "suspend",
  "cancel",
  "prepare-messages",
  "finish-messages-setup",
]);
export type LocalComputerCommand = typeof LocalComputerCommand.Type;

// Public display data only. This projection never supplies authentication.
export const MenuBarAccount = Schema.Struct({
  status: Schema.Literals([
    "checking",
    "signed-in",
    "signed-out",
    "refresh-required",
    "unavailable",
  ]),
  identity: Schema.NullOr(Schema.String.check(Schema.isMaxLength(256))),
  expiresAt: Schema.NullOr(Schema.Number.check(Schema.isFinite())),
  observedAt: Schema.Number.check(Schema.isFinite()),
});
export type MenuBarAccount = typeof MenuBarAccount.Type;

export const MenuBarAction = Schema.Literals([
  "sign-in",
  "refresh-access",
  "sign-out",
  "open-cleo",
  "settings",
  "quit",
]);
export type MenuBarAction = typeof MenuBarAction.Type;

// Why Cleo's Mac could not be restored from its saved state. Present while that save is kept.
export const LocalComputerRestoreFailure = Schema.Struct({
  stage: Schema.Literals(["restore", "resume"]),
  domain: Schema.String,
  code: Schema.Number,
  description: Schema.String,
  hostOSVersion: Schema.String,
  failedAt: Schema.String,
});
export type LocalComputerRestoreFailure = typeof LocalComputerRestoreFailure.Type;

// The saved state Start fresh set aside, kept until the person removes it or a later Start
// fresh replaces it. Never restored. `failure` is null when its record could not be read.
export const LocalComputerSetAsideSave = Schema.Struct({
  failure: Schema.NullOr(LocalComputerRestoreFailure),
  bytes: Schema.Number,
});
export type LocalComputerSetAsideSave = typeof LocalComputerSetAsideSave.Type;

export const LocalComputerState = Schema.Struct({
  protocolVersion: Schema.Literal(1),
  supported: Schema.Boolean,
  state: Schema.Literals([
    "unsupported",
    "unavailable",
    "absent",
    "preparing",
    "downloading",
    "installing",
    "incomplete",
    "stopped",
    "starting",
    "running",
    "paused",
    "suspending",
    "suspended",
    "stopping",
    "error",
  ]),
  id: Schema.NullOr(Schema.String),
  cpuCount: Schema.Number,
  memoryBytes: Schema.Number,
  diskBytes: Schema.Number,
  allocatedDiskBytes: Schema.Number,
  availableDiskBytes: Schema.Number,
  hostMemoryBytes: Schema.Number,
  macOSVersion: Schema.NullOr(Schema.String),
  progress: Schema.NullOr(Schema.Number),
  error: Schema.NullOr(Schema.String),
  identityDigest: Schema.NullOr(Schema.String),
  desktopVisible: Schema.optional(Schema.Boolean),
  menuBarVisible: Schema.optional(Schema.Boolean),
  quickViewVisible: Schema.optional(Schema.Boolean),
  messagesInstallerAvailable: Schema.optional(Schema.Boolean),
  messagesSetupShared: Schema.optional(Schema.Boolean),
  restoreFailure: Schema.optional(Schema.NullOr(LocalComputerRestoreFailure)),
  setAsideSave: Schema.optional(Schema.NullOr(LocalComputerSetAsideSave)),
});
export type LocalComputerState = typeof LocalComputerState.Type;
